import type { RuntimeMediaAudioMetadataResponse } from "@/lib/api/generated/control-api.schemas";

export type RuntimeMediaAudioPolicy = {
  enabled: boolean;
  providerSelection: string;
  model: string | null;
};

export function createDefaultRuntimeMediaAudioPolicy(
  metadata?: RuntimeMediaAudioMetadataResponse | null,
): RuntimeMediaAudioPolicy {
  return {
    enabled: true,
    providerSelection: metadata?.providerSelectionAuto ?? "auto",
    model: null,
  };
}

function readRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
}

function readString(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

function readBoolean(value: unknown, fallback: boolean): boolean {
  return typeof value === "boolean" ? value : fallback;
}

export function readRuntimeMediaAudioPolicy(
  runtimePolicies: Record<string, unknown> | null | undefined,
  metadata?: RuntimeMediaAudioMetadataResponse | null,
): RuntimeMediaAudioPolicy {
  const tools = readRecord(runtimePolicies?.tools);
  const media = readRecord(tools?.media);
  const audio = readRecord(media?.audio);
  if (!audio) {
    return createDefaultRuntimeMediaAudioPolicy(metadata);
  }

  const providerSelectionAuto = metadata?.providerSelectionAuto ?? "auto";
  const providerSelection =
    readString(audio.providerSelection) ?? providerSelectionAuto;
  return {
    enabled: readBoolean(audio.enabled, true),
    providerSelection,
    model: readString(audio.model),
  };
}

export function buildRuntimePoliciesPatch(
  existingPolicies: Record<string, unknown> | null | undefined,
  audioPolicy: RuntimeMediaAudioPolicy,
  metadata?: RuntimeMediaAudioMetadataResponse | null,
): Record<string, unknown> {
  const providerSelectionAuto = metadata?.providerSelectionAuto ?? "auto";
  const next = { ...(existingPolicies ?? {}) };
  const tools = { ...readRecord(next.tools) };
  const media = { ...readRecord(tools.media) };
  const normalizedProviderSelection =
    audioPolicy.providerSelection.trim() || providerSelectionAuto;
  media.audio = {
    enabled: audioPolicy.enabled,
    providerSelection: normalizedProviderSelection,
    model:
      normalizedProviderSelection === providerSelectionAuto
        ? null
        : audioPolicy.model,
  };
  tools.media = media;
  next.tools = tools;
  return next;
}

export function resolveSuggestedAudioModel(
  providerSelection: string,
  metadata: RuntimeMediaAudioMetadataResponse | null | undefined,
): string | null {
  const providerSelectionAuto = metadata?.providerSelectionAuto ?? "auto";
  if (providerSelection === providerSelectionAuto || !metadata) {
    return null;
  }
  return metadata.defaultModelByProviderId[providerSelection] ?? null;
}

export function formatProviderSelectionLabel(
  providerSelection: string,
  metadata?: RuntimeMediaAudioMetadataResponse | null,
): string {
  const providerSelectionAuto = metadata?.providerSelectionAuto ?? "auto";
  if (providerSelection === providerSelectionAuto) {
    return "Auto (default AI provider)";
  }
  return providerSelection;
}
